import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Activity, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LogAttivita } from '../types';
import apiService from '../services/api';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { ColDef } from 'ag-grid-community';
import {
  PageContainerWide,
  PageHeader,
  Card,
  CardBody,
  Button,
  Input,
  Select,
  Alert,
  StatCard,
  StatGrid,
  useConfirm
} from '../components/ui';

const Controls = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled(Input)`
  width: 250px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

// Column definitions per AG Grid
const columnDefs: ColDef[] = [
  {
    headerName: 'Data/Ora',
    field: 'created_at',
    width: 160,
    pinned: 'left',
    sortable: true,
    cellRenderer: (params: any) => {
      const date = new Date(params.value);
      return React.createElement('div', {
        style: { fontSize: '13px', lineHeight: '1.4' }
      }, [
        React.createElement('div', { key: 'date', style: { fontWeight: '600' } },
          date.toLocaleDateString('it-IT')
        ),
        React.createElement('div', { key: 'time', style: { color: 'var(--text-secondary)' } },
          date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
        )
      ]);
    }
  },
  {
    headerName: 'Utente',
    field: 'utente_nome',
    width: 150,
    sortable: true,
    filter: true,
    cellRenderer: (params: any) => {
      return React.createElement('div', {
        style: { fontSize: '13px', lineHeight: '1.4' }
      }, [
        React.createElement('div', { key: 'nome', style: { fontWeight: '600' } },
          params.data.utente_nome || 'Sistema'
        ),
        React.createElement('div', { key: 'email', style: { color: 'var(--text-secondary)', fontSize: '12px' } },
          params.data.utente_email || ''
        )
      ]);
    }
  },
  {
    headerName: 'Azione',
    field: 'azione',
    width: 200,
    sortable: true,
    filter: true,
    cellRenderer: (params: any) => {
      const getActionColor = (azione: string) => {
        if (azione.includes('CREATE') || azione.includes('AGGIUNGI')) return 'var(--success)';
        if (azione.includes('UPDATE') || azione.includes('MODIFICA')) return 'var(--warning)';
        if (azione.includes('DELETE') || azione.includes('ELIMINA')) return 'var(--error)';
        if (azione.includes('LOGIN')) return 'var(--info)';
        return 'var(--gray-500)';
      };

      return React.createElement('span', {
        style: {
          backgroundColor: getActionColor(params.value),
          color: 'white',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500'
        }
      }, params.value);
    }
  },
  {
    headerName: 'Dettagli',
    field: 'dettagli',
    flex: 1,
    minWidth: 250,
    sortable: true,
    filter: true,
    cellRenderer: (params: any) => {
      return React.createElement('div', {
        style: {
          fontSize: '13px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          title: params.value
        }
      }, params.value || 'N/A');
    }
  },
  {
    headerName: 'IP Address',
    field: 'ip_address',
    width: 120,
    sortable: true,
    filter: true,
    cellRenderer: (params: any) => {
      return React.createElement('code', {
        style: {
          fontSize: '12px',
          backgroundColor: 'var(--gray-50)',
          padding: '2px 6px',
          borderRadius: '3px'
        }
      }, params.value || 'N/A');
    }
  }
];

const LogAttivitaPage: React.FC = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [logs, setLogs] = useState<LogAttivita[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    errors: 0
  });
  const [alert, setAlert] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });

  useEffect(() => {
    // Verifica se l'utente è admin
    if (user && user.ruolo !== 'admin') {
      setAlert({ show: true, message: "Non hai i permessi necessari per accedere a questa pagina.", type: 'error' });
      setLoading(false);
      return;
    }

    caricaLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caricamento solo al cambio utente; caricaLogs è ricreata ad ogni render
  }, [user]);

  const caricaLogs = async () => {
    try {
      setLoading(true);
      const data = await apiService.getLogAttivita({ limite: 1000 });
      setLogs(data);
      calcolaStatistiche(data);
    } catch (error) {
      console.error('Errore caricamento log:', error);
      setAlert({ show: true, message: 'Errore nel caricamento dei log', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const calcolaStatistiche = (data: LogAttivita[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const stats = {
      total: data.length,
      today: data.filter(log => new Date(log.created_at) >= today).length,
      thisWeek: data.filter(log => new Date(log.created_at) >= weekAgo).length,
      errors: data.filter(log => log.azione.toLowerCase().includes('error') ||
                                  log.azione.toLowerCase().includes('errore')).length
    };

    setStats(stats);
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' ||
      log.azione.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.dettagli?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.utente_nome?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAction = actionFilter === '' || log.azione.includes(actionFilter);

    return matchesSearch && matchesAction;
  });

  const clearOldLogs = async () => {
    const confirmed = await confirm({
      message: 'Sei sicuro di voler eliminare i log più vecchi di 30 giorni?',
      variant: 'danger'
    });
    if (!confirmed) {
      return;
    }

    try {
      // Logica per eliminare log vecchi - da implementare nell'API
      setAlert({ show: true, message: 'Log vecchi eliminati.', type: 'success' });
      caricaLogs();
    } catch (error) {
      console.error('Errore eliminazione log:', error);
      setAlert({ show: true, message: 'Errore nell\'eliminazione dei log', type: 'error' });
    }
  };

  const getUniqueActions = () => {
    const actions = Array.from(new Set(logs.map(log => log.azione)));
    return actions.sort();
  };

  if (user && user.ruolo !== 'admin') {
    return (
      <PageContainerWide>
        <Alert $variant="error">
          Non hai i permessi necessari per accedere a questa pagina.
        </Alert>
      </PageContainerWide>
    );
  }

  return (
    <PageContainerWide>
      <PageHeader
        title="Log Attività"
        icon={<Activity />}
        actions={
          <Controls>
            <SearchInput
              type="text"
              placeholder="Cerca nei log..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="">Tutte le azioni</option>
              {getUniqueActions().map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </Select>
            <Button $variant="danger" onClick={clearOldLogs}>
              <Trash2 size={16} />
              Pulisci Log Vecchi
            </Button>
          </Controls>
        }
      />

      {alert.show && (
        <Alert $variant={alert.type}>
          {alert.message}
        </Alert>
      )}

      <StatGrid>
        <StatCard value={stats.total} label="Totale attività" />
        <StatCard value={stats.today} label="Attività oggi" />
        <StatCard value={stats.thisWeek} label="Attività questa settimana" />
        <StatCard value={stats.errors} label="Errori registrati" />
      </StatGrid>

      <Card>
        <CardBody>
          <GelatoDataGrid
            rowData={filteredLogs}
            columnDefs={columnDefs}
            loading={loading}
          />
        </CardBody>
      </Card>
    </PageContainerWide>
  );
};

export default LogAttivitaPage;
